/**
 * V2 device detection + PWA install signals (default-app wave 2, step 7).
 *
 * Detection is UA-based and deliberately coarse — it only ever decides
 * which INSTRUCTIONS to show, never behavior that could break a
 * misdetected device. The one trap: modern iPads report as "Macintosh",
 * so a Mac UA with a touchscreen is treated as an iPad.
 *
 * Install signals: Chrome's beforeinstallprompt is captured at app start
 * (it fires once, early — a view mounting later would miss it) so the
 * phone page can offer a REAL install button; appinstalled sets a
 * per-device flag so the invitation card stops showing. Per-device is
 * the right scope — installing on your phone doesn't put Yoink on your
 * iPad. iOS exposes no install events; there the flag is only ever set
 * by the card's own "don't show again" dismissal.
 */

export const APP_INSTALLED_KEY = 'yoink_app_installed';

export function isAndroid(ua = navigator.userAgent) {
  return /android/i.test(ua);
}

export function isIOS(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints || 0) {
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ masquerades as a Mac; the touchscreen gives it away
  return /macintosh/i.test(ua) && touchPoints > 1;
}

export function isMobile(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints || 0) {
  return isAndroid(ua) || isIOS(ua, touchPoints);
}

/** Running as the installed app (Home Screen / PWA window)? */
export function isStandalone() {
  return Boolean(
    window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.navigator.standalone // iOS Safari's own flag
  );
}

export function appInstalled() {
  try {
    return localStorage.getItem(APP_INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markAppInstalled() {
  try {
    localStorage.setItem(APP_INSTALLED_KEY, '1');
  } catch { /* private mode — the card just shows again next visit */ }
}

let deferredInstall = null;

/** Called once from main.js start() — see header for why so early. */
export function captureInstallSignals() {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chrome's own mini-infobar; the phone page's button asks
    event.preventDefault();
    deferredInstall = event;
  });
  window.addEventListener('appinstalled', () => {
    deferredInstall = null;
    markAppInstalled();
  });
  // Opened AS the installed app → it's installed on this device
  if (isStandalone()) markAppInstalled();
}

/** Chrome's captured install prompt, when one is on offer. */
export function canPromptInstall() {
  return Boolean(deferredInstall);
}

/** Show Chrome's install dialog. Resolves true when they accepted. */
export async function promptInstall() {
  if (!deferredInstall) return false;
  const prompt = deferredInstall;
  deferredInstall = null; // a prompt event is single-use
  prompt.prompt();
  const choice = await prompt.userChoice.catch(() => null);
  const accepted = choice?.outcome === 'accepted';
  if (accepted) markAppInstalled();
  return accepted;
}
